import React, { FC, useEffect, useState } from 'react';
import { Form, Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ContainerWithForm } from '../../components/Container';
import EwayPaymentForm from './EwayPaymentForm';
import FeesAndCancellationPolicy from './FeesAndCancellationPolicy';
import FooterNavigator from '../../components/FooterNavigator';
import { MEDICARE, Confirmation } from '../../constants/routes';
import { createTokenCustomerOnEway, fetchTokenCustomerCreationResult } from '../../services/payment';
import { RequestStatus } from '../../model';
import { addConsentsData, addEwayPaymentData, selectBookingData } from '../../redux/slices/booking';
import { CONSENTS_MAPPING } from '../../constants/consents';
import { useAppDispatch } from '../../hooks/redux-hooks';
import analytics from '../../infra/analytics';
import {
  PAYMENT_CONSENT_AND_POLICY, SITE_NAME,
} from '../../constants/analytics';
import { StylePageSubtitle, StylePageTitle } from '../../styles/sharedStyleComponets';

const CreditCardDetails: FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const bookingData = useSelector(selectBookingData);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [eWayAccessCode, setEWayAccessCode] = useState('');
  const [eWayFormActionURL, setEWayFormActionURL] = useState('');

  const [isAgreedTheFees, setIsAgreedTheFees] = useState(false);
  const [isAgreedTheCancelPolicy, setIsAgreedTheCancelPolicy] = useState(false);

  const { pathname } = useLocation();

  useEffect(() => {
    analytics.logPageViewEvent(PAYMENT_CONSENT_AND_POLICY, {
      site: SITE_NAME,
      page_path: pathname,
    });
  }, [pathname]);

  const submit = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    if (!isAgreedTheFees || !isAgreedTheCancelPolicy) {
      setErrorMessage(`Please agree to the ${!isAgreedTheCancelPolicy ? 'cancellation policy' : 'fees policy'} before continuing.`);
      setIsLoading(false);

      analytics.trackVpcFormError({
        form_id: bookingData.formId,
        error_type: 'payment_failed',
        page_path: pathname,
      });

      return false;
    }

    try {
      dispatch(addConsentsData({
        isAgreedTheFees: { ...CONSENTS_MAPPING.isAgreedTheFees, agreed: true },
        isAgreedTheCancelPolicy: { ...CONSENTS_MAPPING.isAgreedTheCancelPolicy, agreed: true },
      }));
      const values = await form.validateFields();
      const { EWAY_CARDEXPIRYDATE, ...finalValues } = values;
      await createTokenCustomerOnEway(eWayFormActionURL, {
        ...finalValues,
        EWAY_ACCESSCODE: eWayAccessCode,
        EWAY_PAYMENTTYPE: 'Credit Card',
        EWAY_CARDEXPIRYMONTH: parseInt(EWAY_CARDEXPIRYDATE.substring(0, 2), 10),
        EWAY_CARDEXPIRYYEAR: parseInt(EWAY_CARDEXPIRYDATE.substring(3, 7), 10),
      });
      const { status, data } = await fetchTokenCustomerCreationResult(eWayAccessCode);
      if (status === RequestStatus.SUCCESS) {
        dispatch(addEwayPaymentData({
          customerId: data.tokenCustomerID,
        }));
        return true;
      }

      analytics.trackVpcFormError({
        form_id: bookingData.formId,
        error_type: 'payment_failed',
        page_path: pathname,
      });

      setErrorMessage('Setup payment method failed. Please try again later or contact Telecare at (03 9052 4872)');
      return false;
    } catch (e) {
      analytics.trackVpcFormError({
        form_id: bookingData.formId,
        error_type: 'server_error',
        page_path: pathname,
      });

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContainerWithForm>
      <StylePageTitle level={2}>
        Payment Consent and Cancellation Policy
      </StylePageTitle>
      <StylePageSubtitle>
        To proceed with a telehealth appointment, the Amplar Health Virtual Psychology team will need to collect credit details from you.
      </StylePageSubtitle>
      <StylePageSubtitle>
        You will not be charged unless an appointment takes place or if you do not meet our cancellation policy below.
      </StylePageSubtitle>
      <EwayPaymentForm form={form} setEWayAccessCode={setEWayAccessCode} setEWayFormActionURL={setEWayFormActionURL} />

      <div id="eWAYBlock">
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <a
            href="http://www.eway.com.au/secure-site-seal?i=12&s=7&pid=1e5372f4-5b37-4cc5-a301-03839da191b8&theme=0"
            title="Eway Payment Gateway"
            target="_blank"
            rel="nofollow noreferrer"
          >
            <img
              alt="Eway Payment Gateway"
              src="https://www.eway.com.au/developer/payment-code/verified-seal.php?img=12&size=7&pid=1e5372f4-5b37-4cc5-a301-03839da191b8&theme=0"
            />
          </a>
        </div>
      </div>

      <FeesAndCancellationPolicy
        setIsAgreedTheFees={setIsAgreedTheFees}
        setIsAgreedTheCancelPolicy={setIsAgreedTheCancelPolicy}
        isAgreedTheFees={isAgreedTheFees}
        isAgreedTheCancelPolicy={isAgreedTheCancelPolicy}
      />

      {errorMessage && <Typography.Paragraph type="danger">{errorMessage}</Typography.Paragraph>}
      <FooterNavigator submit={submit} error="" next={Confirmation} back={MEDICARE} containerStyle={{ marginTop: '30px' }} loading={isLoading} />
    </ContainerWithForm>
  );
};

export default CreditCardDetails;
